import React, { useMemo } from 'react';
import { Plus, PencilSimple, Trash, CircleNotch } from '@phosphor-icons/react';
import {
    getObligationStats,
    getNextUnpaidPayment,
    getTotalUnpaidAmount,
    formatEndDate,
    fmtPLN,
} from './obligationUtils';
import MarkMonthPaidButton from './MarkMonthPaidButton';

export default function LeasingsTab({
    leasings,
    loading,
    onAddLeasing,
    onEditLeasing,
    onDeleteLeasing,
    onRefresh,
}) {
    const summary = useMemo(() => {
        let nextSum = 0;
        let totalUnpaid = 0;
        let unpaidCount = 0;
        for (const l of leasings) {
            const next = getNextUnpaidPayment(l.payments);
            if (next) nextSum += next.amount;
            totalUnpaid += getTotalUnpaidAmount(l.payments);
            unpaidCount += (Array.isArray(l.payments) ? l.payments.filter(p => !p?.paid).length : 0);
        }
        return { nextSum, totalUnpaid, unpaidCount, count: leasings.length };
    }, [leasings]);

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading text-xl font-bold">Leasingi ({leasings.length})</h2>
                <button
                    onClick={() => onAddLeasing('leasing')}
                    className="btn-primary flex items-center gap-2"
                    data-testid="add-leasing-btn"
                >
                    <Plus className="w-4 h-4" /> Dodaj leasing
                </button>
            </div>
            <div className="mb-4">
                <MarkMonthPaidButton
                    scope="leasing"
                    scopeLabel="leasingach"
                    onDone={onRefresh}
                    testid="mark-paid-leasing"
                />
            </div>
            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <CircleNotch className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
            ) : leasings.length === 0 ? (
                <div className="text-center py-20 text-muted-foreground">
                    Brak leasingów. Dodaj pierwszy!
                </div>
            ) : (
                <div className="bg-white border border-border rounded-sm overflow-x-auto -mx-3 sm:mx-0">
                    <table className="w-full min-w-[900px]">
                        <thead className="bg-muted">
                            <tr>
                                <th className="text-left py-3 px-4 field-label">Pojazd</th>
                                <th className="text-right py-3 px-4 field-label">Raty (opł. / razem)</th>
                                <th className="text-right py-3 px-4 field-label">Pozostało</th>
                                <th className="text-right py-3 px-4 field-label">Kwota najbliższej raty</th>
                                <th className="text-right py-3 px-4 field-label">Data zakończenia</th>
                                <th className="text-right py-3 px-4 field-label">Akcje</th>
                            </tr>
                        </thead>
                        <tbody>
                            {leasings.map(leasing => {
                                const { paid, total, remaining, endDate } = getObligationStats(leasing.payments);
                                const next = getNextUnpaidPayment(leasing.payments);
                                return (
                                    <tr key={leasing.id} className="table-row" data-testid={`leasing-row-${leasing.id}`}>
                                        <td className="py-3 px-4 font-medium">{leasing.name}</td>
                                        <td className="py-3 px-4 text-right tabular-nums">{paid} / {total}</td>
                                        <td className="py-3 px-4 text-right">
                                            <span
                                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${remaining === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}
                                                data-testid={`leasing-remaining-${leasing.id}`}
                                            >
                                                {remaining} {remaining === 1 ? 'rata' : 'rat'}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-right tabular-nums font-mono" data-testid={`leasing-next-${leasing.id}`}>
                                            {next ? (
                                                <div>
                                                    <div className="font-semibold text-slate-800">{fmtPLN(next.amount)}</div>
                                                    {next.date && (
                                                        <div className="text-[10px] text-slate-500">
                                                            {next.date} · #{next.payment_number}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-emerald-600 text-xs font-semibold">— spłacone</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right text-sm text-slate-700 tabular-nums" data-testid={`leasing-enddate-${leasing.id}`}>
                                            {formatEndDate(endDate)}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <button onClick={() => onEditLeasing(leasing, 'leasing')} className="p-2 hover:bg-muted rounded-sm">
                                                <PencilSimple className="w-4 h-4" />
                                            </button>
                                            <button onClick={() => onDeleteLeasing(leasing.id, 'leasing')} className="p-2 hover:bg-red-50 text-red-600 rounded-sm">
                                                <Trash className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot className="bg-emerald-50 border-t-2 border-emerald-300">
                            <tr>
                                <td colSpan={3} className="py-3 px-4 text-right text-xs font-bold text-emerald-800 uppercase tracking-wider">
                                    Podsumowanie ({summary.count} {summary.count === 1 ? 'leasing' : 'leasingów'}):
                                </td>
                                <td className="py-3 px-4 text-right">
                                    <div className="text-xs text-slate-600">Najbliższe raty razem:</div>
                                    <div className="text-base font-bold tabular-nums font-mono text-emerald-700" data-testid="leasings-next-sum">
                                        {fmtPLN(summary.nextSum)}
                                    </div>
                                </td>
                                <td colSpan={2} className="py-3 px-4 text-right">
                                    <div className="text-xs text-slate-600">Wszystkie nieopłacone razem ({summary.unpaidCount}):</div>
                                    <div className="text-base font-bold tabular-nums font-mono text-slate-800" data-testid="leasings-total-unpaid">
                                        {fmtPLN(summary.totalUnpaid)}
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}
